import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import utils from '../../../utils';
import './AdFLStreams.css';

/** API may return camelCase or PascalCase; normalise to one object. */
function getLiveFeedDisplay(product) {
  return product.liveFeedDisplay || product.LiveFeedDisplay || null;
}

/**
 * Parse start from product to milliseconds, or null.
 * Uses: Start (Unix seconds or ISO), or streamDate + streamTime (e.g. "2026-03-22" + "11:00") so future dates show as Upcoming.
 */
function getStartMs(product) {
  const display = getLiveFeedDisplay(product);
  const start = display?.start ?? display?.Start;
  if (start != null && start !== '') {
    const num = Number(start);
    if (Number.isFinite(num) && num > 0) return num * 1000;
    try {
      const ms = new Date(start).getTime();
      if (Number.isFinite(ms)) return ms;
    } catch { /* fall through to date+time */ }
  }
  const d = display?.streamDate ?? display?.StreamDate;
  const t = display?.streamTime ?? display?.StreamTime;
  if (!d || d === '') return null;
  try {
    const dateStr = t && t !== '' ? `${d}T${t}:00` : `${d}T12:00:00`;
    const ms = new Date(dateStr).getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

function formatStreamDateTime(product) {
  const display = getLiveFeedDisplay(product);
  const d = display?.streamDate ?? display?.StreamDate;
  const t = display?.streamTime ?? display?.StreamTime;
  if (d || t) return [d, t].filter(Boolean).join(' ');
  const ms = getStartMs(product);
  if (ms == null) return null;
  try {
    return new Date(ms).toLocaleString('en-AU', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

/** Readable tile-style date/time like "JUL 26, 2025, 02:00PM". */
function formatStreamDateTimeTile(product) {
  const ms = getStartMs(product);
  if (ms == null) return null;
  try {
    const date = new Date(ms);
    const month = date.toLocaleString('en-AU', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    const year = date.getFullYear();
    const h = date.getHours();
    const m = date.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    const time = `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}${ampm}`;
    return `${month} ${day}, ${year}, ${time}`;
  } catch {
    return null;
  }
}

function getPriceLabel(product) {
  const price = product.priceIncTax ?? product.PriceIncTax ?? product.price ?? product.Price;
  const num = Number(price);
  if (Number.isFinite(num) && num <= 0) return 'Free';
  if (Number.isFinite(num)) return null; // will show formatted price
  return 'Paid';
}

function isLiveNow(startMs) {
  if (startMs == null) return false;
  const startTime = new Date(startMs);
  const now = new Date();
  return now >= startTime && now.getTime() - startTime.getTime() < 4 * 60 * 60 * 1000;
}

function isUpcoming(startMs) {
  if (startMs == null) return false;
  return new Date(startMs) > new Date();
}

function isSeasonPassProduct(product) {
  const name = (product.productName || product.ProductName || '').toLowerCase();
  return /season pass|all season/.test(name);
}

function groupByTime(items) {
  const withStart = items.map((p) => ({ product: p, startMs: getStartMs(p) }));
  const live = withStart.filter(({ startMs }) => isLiveNow(startMs));
  const upcoming = withStart.filter(({ startMs }) => isUpcoming(startMs));
  const past = withStart.filter(({ startMs }) => !isLiveNow(startMs) && !isUpcoming(startMs));
  const hasAnyGrouping = live.length > 0 || upcoming.length > 0 || past.length > 0;
  return {
    liveStreams: live.map(({ product }) => product),
    upcomingStreams: upcoming.map(({ product }) => product),
    pastStreams: past.map(({ product }) => product),
    allStreams: hasAnyGrouping ? [] : items,
  };
}

/**
 * AdFL Live Streams – Season Pass and Single Game in separate sections; each with Live Now, Upcoming, Past.
 */
export function AdFLStreams({ items = [], watchTokensByProductId = {}, seasonPassToken = null, pageTitle = 'Live Streams' }) {
  const { seasonPass, singleGame } = useMemo(() => {
    const seasonPassItems = items.filter(isSeasonPassProduct);
    const singleGameItems = items.filter((p) => !isSeasonPassProduct(p));
    return {
      seasonPass: groupByTime(seasonPassItems),
      singleGame: groupByTime(singleGameItems),
    };
  }, [items]);

  const getWatchToken = (product) => {
    const id = (product.productId || product.ProductId)?.toString();
    return watchTokensByProductId[id] ?? seasonPassToken ?? null;
  };

  const renderBlock = (block) => (
    <>
      {block.liveStreams.length > 0 && (
        <div className="ff-section">
          <h3 style={{ color: '#d32f2f', marginBottom: 12 }}>🔴 LIVE NOW</h3>
          <div className="ff-grid">
            {block.liveStreams.map((product) => (
              <StreamCard key={(product.productId || product.ProductId)?.toString()} product={product} watchToken={getWatchToken(product)} isLive />
            ))}
          </div>
        </div>
      )}
      {block.upcomingStreams.length > 0 && (
        <div className="ff-section">
          <h3 style={{ marginBottom: 12 }}>UPCOMING</h3>
          <div className="ff-grid">
            {block.upcomingStreams.map((product) => (
              <StreamCard key={(product.productId || product.ProductId)?.toString()} product={product} watchToken={getWatchToken(product)} isUpcoming />
            ))}
          </div>
        </div>
      )}
      {block.pastStreams.length > 0 && (
        <div className="ff-section">
          <h3 style={{ marginBottom: 12 }}>PAST STREAMS</h3>
          <div className="ff-grid">
            {block.pastStreams.map((product) => (
              <StreamCard key={(product.productId || product.ProductId)?.toString()} product={product} watchToken={getWatchToken(product)} isPast />
            ))}
          </div>
        </div>
      )}
      {block.allStreams.length > 0 && (
        <div className="ff-section">
          <div className="ff-grid">
            {block.allStreams.map((product) => (
              <StreamCard key={(product.productId || product.ProductId)?.toString()} product={product} watchToken={getWatchToken(product)} />
            ))}
          </div>
        </div>
      )}
    </>
  );

  const hasSeasonPass = seasonPass.liveStreams.length > 0 || seasonPass.upcomingStreams.length > 0 || seasonPass.pastStreams.length > 0 || seasonPass.allStreams.length > 0;
  const hasSingleGame = singleGame.liveStreams.length > 0 || singleGame.upcomingStreams.length > 0 || singleGame.pastStreams.length > 0 || singleGame.allStreams.length > 0;

  return (
    <div className="ff-home">
      <div className="ff-titleRow" style={{ marginBottom: 20 }}>
        <h2>{pageTitle}</h2>
      </div>

      {/* <div className="ff-muted" style={{ marginBottom: 24 }}>
        Purchase a stream to watch. If you already have access, use Watch Now to open the stream.
      </div> */}

      {items.length === 0 && (
        <div className="ff-muted">No streams available at this time.</div>
      )}

      {hasSeasonPass && (
        <div className="ff-typeBlock">
          <h3 className="ff-typeTitle">Season Pass</h3>
          {renderBlock(seasonPass)}
        </div>
      )}

      {hasSingleGame && (
        <div className="ff-typeBlock">
          <h3 className="ff-typeTitle">Single Game</h3>
          {renderBlock(singleGame)}
        </div>
      )}
    </div>
  );
}

function StreamCard({ product, watchToken, isLive = false, isUpcoming = false, isPast = false }) {
  const display = getLiveFeedDisplay(product);
  const productId = (product.productId ?? product.ProductId)?.toString();
  const productUrl = product.productUrl ? product.productUrl : `/product/${product.productSlug}`;
  /* Same image source as normal product tile: thumbnailUrl first (from ProductView), then imageUrl (first ProductImage), then LiveFeed poster. Skip empty strings. */
  const pickFirst = (...vals) => vals.find((v) => v != null && String(v).trim() !== '') || null;
  const rawPoster = pickFirst(
    product.thumbnailUrl,
    product.ThumbnailUrl,
    product.imageUrl,
    product.ImageUrl,
    display?.posterUrl,
    display?.PosterUrl,
    product.productImages?.[0]?.imageUrl,
    product.productImages?.[0]?.ImageUrl
  );
  const posterUrl = rawPoster ? (rawPoster.startsWith('http') ? rawPoster : utils.site.resourcePath(rawPoster)) : '/assets/product-empty.png';
  const title = (display?.title ?? display?.Title)?.trim() || product.productName || product.ProductName || 'Live stream';
  const dateTimeStr = formatStreamDateTimeTile(product) || formatStreamDateTime(product) || 'Date & time TBC';
  const priceLabel = getPriceLabel(product);
  const priceNum = Number(product.priceIncTax ?? product.PriceIncTax ?? product.price ?? product.Price);
  const priceText = priceLabel === 'Free' ? 'Free' : Number.isFinite(priceNum) && priceNum > 0
    ? `AU $${priceNum.toFixed(2)}`
    : priceLabel || null;

  /* If user has access → Watch; otherwise → checkout (login/signup handled there). Removes product-page layer. Fallback to product page if no productId. */
  const hasWatch = !!watchToken;
  const ctaUrl = hasWatch
    ? `/watch?t=${encodeURIComponent(watchToken)}`
    : (productId ? `/checkout/live-feed?productId=${encodeURIComponent(productId)}` : productUrl);
  const ctaLabel = hasWatch ? 'Watch Now' : 'Subscribe Now';

  return (
    <Link to={ctaUrl} className="ff-card ff-cardLink">
      <div className="ff-cardBanner">
        <img src={posterUrl} alt="" loading="lazy" />
      </div>
      <div className="ff-cardBody">
        <div className="ff-cardPillDate">
          <span className="ff-cardPills">
            {isLive && <span className="ff-livePill">live</span>}
            {isUpcoming && !isLive && <span className="ff-upcomingPill">Upcoming</span>}
            {isPast && !isLive && !isUpcoming && <span className="ff-pastPill">Past</span>}
          </span>
          <span className="ff-cardDateTime">{dateTimeStr}</span>
        </div>

        <h3 className="ff-cardTitle">{title}</h3>
        <div className="ff-cardPriceBtn">
          {hasWatch ? (
            <span className="ff-cardSubscribed">Subscribed</span>
          ) : (
            priceText != null && <span className="ff-cardPrice">{priceText}</span>
          )}
          <span className="ff-cardWatchBtn">{ctaLabel}</span>
        </div>
      </div>
    </Link>
  );
}
